import * as THREE from 'three';
import { TextGeometry } from 'src/lib/TextGeometry.js';
import { SVGLoader } from 'src/lib/SVGLoader.js';
import { getMinMax } from 'src/utils/booth_functions';
import rtlText from '@mapbox/mapbox-gl-rtl-text';

let applyArabicShaping: CallableFunction;
let processBidirectionalText: CallableFunction;
if (typeof window !== 'undefined') {
	void (async () => {
		const rtl = await rtlText;
		applyArabicShaping = rtl.applyArabicShaping;
		processBidirectionalText = rtl.processBidirectionalText;
	})();
}

export function createCube(width = 1, height = 1, depth = 1, pos = { x: 0, y: 0, z: 0 }, color = 0xffffff) {
	const geometry = new THREE.BoxGeometry(width, height, depth);
	const material = new THREE.MeshPhongMaterial({ color, flatShading: true });
	const cube = new THREE.Mesh(geometry, material);

	cube.position.set(pos.x, pos.y, pos.z);

	return cube;
}

export function createSphere(size = 1, pos = { x: 0, y: 0, z: 0 }, color = 0xffffff) {
	const geometry = new THREE.SphereGeometry(size);
	const material = new THREE.MeshBasicMaterial({ color });
	const sphere = new THREE.Mesh(geometry, material);

	sphere.position.set(pos.x, pos.y, pos.z);

	return sphere;
}

export function createCylinder(radius = 1, height = 1, segments = 32, pos = { x: 0, y: 0, z: 0 }, color = 0xffffff) {
	const geometry = new THREE.CylinderGeometry(radius, radius, height, segments);
	const material = new THREE.MeshBasicMaterial({ color });
	const cylinder = new THREE.Mesh(geometry, material);

	cylinder.position.set(pos.x, pos.y, pos.z);

	return cylinder;
}

export function createLine(p1, p2, color = 0xffffff) {
	const points = [new THREE.Vector3(p1.x, p1.y, p1.z), new THREE.Vector3(p2.x, p2.y, p2.z)];
	const geometry = new THREE.BufferGeometry().setFromPoints(points);
	const material = new THREE.LineBasicMaterial({ color });

	return new THREE.Line(geometry, material);
}

export function createText(text, font, size, depth, pos = { x: 0, y: 0, z: 0 }, color, { align = 'center' } = {}) {
	// 1) RTL shaping + bidi
	const shaped = applyArabicShaping(text);
	const ready = processBidirectionalText(shaped, [])[0];

	// 2) Build geometry
	const geometry = new TextGeometry(ready, {
		font,
		size,
		height: depth, // keep your name -> API's 'height'
		curveSegments: 8,
		bevelEnabled: false,
	});

	// 3) Compute bbox & translate for alignment
	geometry.computeBoundingBox();
	const bb = geometry.boundingBox; // THREE.Box3
	const sizeVec = new THREE.Vector3().subVectors(bb.max, bb.min);
	const center = new THREE.Vector3().addVectors(bb.min, bb.max).multiplyScalar(0.5);

	// Common alignments. 'center' puts the box center at the local origin (0,0,0).
	if (align === 'center') {
		geometry.translate(-center.x, -center.y, -center.z);
	} else if (align === 'left') {
		// left baseline-ish: X at 0, baseline near Y=0, Z centered
		geometry.translate(-bb.min.x, -bb.min.y, -center.z);
	} else if (align === 'right') {
		geometry.translate(-bb.max.x, -bb.min.y, -center.z);
	} else if (align === 'topLeft') {
		geometry.translate(-bb.min.x, -bb.max.y, -center.z);
	} else if (align === 'bottomLeft') {
		geometry.translate(-bb.min.x, -bb.min.y, -center.z);
	} else {
		// default to center if unknown
		geometry.translate(-center.x, -center.y, -center.z);
	}

	// 4) Material + mesh
	const material = new THREE.MeshBasicMaterial({ color });
	const mesh = new THREE.Mesh(geometry, material);

	// 5) Your rotation (lays text flat on XZ plane). Because we centered first,
	//    the rotation keeps it centered.
	mesh.rotateX((-90 * Math.PI) / 180);

	// 6) Place so that pos is the visual center (for align='center')
	mesh.position.set(pos.x, pos.y, pos.z);

	// 7) Expose dimensions (pre-rotation box size; post-rotation it's the same numbers)
	mesh.userData.dimensions = {
		width: sizeVec.x, // text advance (left↔right)
		height: sizeVec.y, // glyph vertical extent (baseline to ascender/descender)
		depth: sizeVec.z, // extrusion depth
	};

	return mesh;
}

export function parseSVG(svg) {
	const loader = new SVGLoader();
	const svgData = loader.parse(svg);

	const output: any[] = [];

	svgData.paths.forEach((path) => {
		let label, nodeId;

		if (path.userData?.node?.id) {
			const [id, node] = path.userData.node.id.split('|');

			if (id) {
				label = id.replace(/_/g, ' ');
			}

			if (node) {
				nodeId = parseInt(node, 10);
			}
		}

		const pathShapes = path.toShapes(!THREE.ShapeUtils.isClockWise(path.subPaths[0].getPoints()));

		output.push({
			label,
			dataset: path.userData.node.dataset,
			nodeId,
			shapes: pathShapes,
		});
	});

	return output;
}

export function makeShape(points: any[]) {
	const { minX, minY, maxX, maxY } = getMinMax(points);

	const vectors = points.map((point) => new THREE.Vector2(point.x - minX, point.y - minY));
	const shape = new THREE.Shape(vectors);

	return [
		shape,
		{
			x: minX,
			y: minY,
		},
		{
			width: maxX - minX,
			height: maxY - minY,
		},
	];
}

export function extrudeShape(shape, config: any = { depth: 1 }) {
	const geometry = new THREE.ExtrudeGeometry(shape, {
		depth: config.depth,
		bevelEnabled: !!config.bevel,
		bevelOffset: config.bevel?.offset,
		bevelThickness: config.bevel?.thickness,
		bevelSegments: config.bevel?.segments,
	});

	geometry.rotateX((90 * Math.PI) / 180);
	geometry.computeVertexNormals();

	return geometry;
}

export function makeExtrusionMaterial(config, basic = false) {
	if (basic) {
		return [
			new THREE.MeshBasicMaterial({ color: config.faceColor }),
			new THREE.MeshBasicMaterial({ color: config.extrusionColor }),
		];
	}

	return [
		new THREE.MeshPhongMaterial({ color: config.faceColor }),
		new THREE.MeshPhongMaterial({ color: config.extrusionColor }),
	];
}
